// SPDX-License-Identifier: Elastic-2.0
/**
 * ErrorBoundary — top-level React error boundary.
 *
 * Catches any error thrown by a descendant render / lifecycle and shows
 * a sanitized fallback page. Per the task spec + ADR-004, we MUST NOT
 * leak the raw error message to the user — Home Assistant is invisible
 * and a stack trace would break that illusion. The full error is logged
 * to `console.error` (dev) and held in state for a future telemetry
 * hook; the rendered UI shows only translated copy.
 *
 * Class component is mandatory: error boundaries cannot be expressed
 * with hooks today (React 18). Strings are i18n keys.
 *
 * Reload behavior: in environments where `window.location.reload` is
 * available, the button reloads the page. The handler is defensive —
 * during tests we tolerate the absence of `window.location` by
 * no-op'ing instead of throwing.
 */
import { Component } from "react";
import type { ComponentType, ErrorInfo, ReactNode } from "react";
import { withTranslation } from "react-i18next";
import type { WithTranslation } from "react-i18next";

export interface ErrorBoundaryOwnProps {
  children: ReactNode;
  /** Optional custom fallback renderer. Receives the caught error so
   *  callers can inspect it, but should NOT include it in user-visible
   *  output. Defaults to the sanitized built-in fallback. */
  fallback?: (error: Error) => ReactNode;
  /** Hook for future telemetry. Called once per caught error. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

type ErrorBoundaryProps = ErrorBoundaryOwnProps & WithTranslation;

class ErrorBoundaryImpl extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Dev console — eslint disable mirrors the i18n module pattern.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught error:", error, info);
    this.props.onError?.(error, info);
  }

  private handleReload = (): void => {
    if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
      window.location.reload();
    }
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(error);
    }

    const { t } = this.props;
    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--space-7)",
          background: "var(--bg)",
          color: "var(--fg-1)",
          fontFamily: "var(--font-body)",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            padding: "var(--space-6)",
            borderRadius: "var(--radius-4)",
            background: "var(--bg-elev-1)",
            border: "1px solid var(--border-1)",
            boxShadow: "var(--shadow-2)",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: "var(--t-h5)",
              lineHeight: "var(--lh-snug)",
              margin: 0,
              marginBottom: "var(--space-3)",
            }}
          >
            {t("errors.boundary_title")}
          </h2>
          <p
            style={{
              color: "var(--fg-2)",
              margin: 0,
              marginBottom: "var(--space-5)",
              fontSize: "var(--t-body-sm)",
              lineHeight: "var(--lh-loose)",
            }}
          >
            {t("errors.boundary_message")}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              appearance: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "var(--space-3) var(--space-5)",
              borderRadius: "var(--radius-pill)",
              border: "1px solid transparent",
              background: "var(--fg-1)",
              color: "var(--bg)",
              fontFamily: "var(--font-body)",
              fontWeight: 500,
              fontSize: "var(--t-body-sm)",
              cursor: "pointer",
            }}
          >
            {t("errors.boundary_reload")}
          </button>
        </div>
      </div>
    );
  }
}

/**
 * `withTranslation()` wraps the class so `this.props.t` is available.
 * This is the React-recommended pattern for using i18n inside a class
 * error boundary (hooks are forbidden in class components).
 */
export const ErrorBoundary = withTranslation()(
  ErrorBoundaryImpl,
) as unknown as ComponentType<ErrorBoundaryOwnProps>;
